<template>
    <div class="prior-authorization-list-view">
        <Card title="Prior Authorizations">
            <div class="stat-grid">
                <div>
                    <b>Total Open:</b>
                    <h4>{{ totalPas }}</h4>
                </div>
                <div v-for="statEnum in paStatusList" :key="statEnum">
                    <span
                        v-b-tooltip.hover="{ title: getManufacturersByStatusHoverText(statEnum), html: true, customClass: 'longToolTip' }">
                        <b>{{ paCodes[statEnum] }}:</b>
                        <h4>{{ countByStatus(statEnum) }}</h4>
                    </span>
                </div>
            </div>
            <hr>
            <div class="filter-grid mb-2">
                <div>
                    <span>Manufacturers</span>
                    <b-form-select v-model="selectedManufacturer" :options="manufacturers" class="mt-1" />
                </div>

                <EnumSelector v-model="status" label="Status" :enum-codes="paCodes" :clearable="true"
                    :elementsToDiscard=statusToIgnore :displayNames="[{}]" />

                <EnumSelector v-model="selectedType" label="Type" :enum-codes="paTypeCodes" :clearable="true" />

                <SearchComponent v-model="store" label="Store" search-u-r-l="/store" prepopulate clearable />

                <SearchComponent v-model="program" label="Program" search-u-r-l="/Program" prepopulate clearable />

                <b-form-group label="Filter">
                    <b-input placeholder="Prescriber Name / Patient Name / Drug Name / CMM Event" v-model="filterQuery"
                        style="padding: 8px 15px; margin-top: -3px;" />
                </b-form-group>
            </div>
            <div v-if="isLoading">
                <b-spinner label="Spinning" class="mr-2"></b-spinner>
                <strong>Loading...</strong>
            </div>
            <div ref="tableBlock" v-else>
                <b-row>
                    <b-col cols="12">
                        <b-table id="all-pending-pa-table" striped outlined responsive="true" sort-icon-left
                            :items="filteredPriorAuthorizations" :fields="fields" primary-key="id"
                            class="cursor-pointer" @row-clicked="selectPa" :per-page="pageSize"
                            :current-page="pageNumber" @sort-changed="resetPage">
                            <template #cell(patient)="data">
                                <div>
                                    {{ `${data.value.lastName}, ${data.value.firstName}` }}
                                </div>
                                <div>
                                    {{ formatDate(data.value.dateOfBirth) }}
                                </div>
                            </template>
                            <template #cell(prescriber)="data">
                                <div>
                                    {{ `${data.value.lastName}, ${data.value.firstName}` }}
                                </div>
                                <div>
                                    {{ data.value.phoneNumber | formatPhone }}
                                </div>
                                <b-badge v-if="!data.value.allowsPa" variant="danger">
                                    Does Not Allow PA
                                </b-badge>
                            </template>
                            <template #cell(statusName)="data">
                                <span>
                                    {{ data.value }}
                                </span>
                            </template>
                        </b-table>

                        <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize" show-total
                            item-name="prior auth" :loading="isLoading" />
                    </b-col>
                </b-row>
            </div>
        </Card>
        <PriorAuthorizationDetails id="pa-details-modal" :value="selectedPa" />
        <PatientDocumentsModal ref="patient-documents-modal" v-if="selectedPa?.patient" :patient="selectedPa.patient" />
    </div>
</template>

<script lang="ts">
import { Vue, Component, Watch, Ref, Prop } from "vue-property-decorator";
import Card from "@/components/Cards/Card.vue";
import PriorAuthorizationDetails from "@/components/PriorAuthorization/PriorAuthorizationDetails.vue";
import EnumSelector from "@/components/EnumSelector.vue";
import moment from 'moment';
import { PriorAuthorizationStatusCode, PriorAuthorization, CoverMyMedsCallback, PriorAuthNextTouch, PriorAuthorizationType } from '@/models/PriorAuthorization';
import { Prescription } from '@/models/Prescription';
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import PatientDocumentsModal from "@/components/Patient/PatientDocumentsModal.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import SearchComponent from "@/components/SearchComponent.vue";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { addBusinessDays } from "@/util/DateUtils";

@Component({
    components: {
        Card,
        PriorAuthorizationDetails,
        EnumSelector,
        BottomPagerBar,
        PatientDocumentsModal,
        SearchComponent
    }
})
export default class PriorAuthorizationListPage extends Vue {
    @Prop({ default: null }) protected status!: string | null;
    @Prop({ default: null }) protected storeId!: string | null;
    @Prop({ default: null }) protected programId!: string | null;

    created() {
        this.getNextTouchDaysFromConfig();
        this.fetchPriorAuthorizations();

        if (this.storeId) this.store = Object.assign(new Store(), { id: parseInt(this.storeId) });
        if (this.programId) this.program = Object.assign(new Program(), { id: parseInt(this.programId) });
    }

    get totalPas(): number {
        return this.priorAuthorizations.length;
    }

    private paCodes = PriorAuthorizationStatusCode;
    private paTypeCodes = PriorAuthorizationType;
    private isLoading = false;
    private selectedManufacturer: string = "All";
    private selectedType: string | null = null;
    private selectedPa: PriorAuthorization = {} as PriorAuthorization;
    private filterQuery: string | null = "";

    private store: Store = new Store();
    private program: Program = new Program();

    private priorAuthorizations: Array<PriorAuthorization> = [];

    private pageSize = 100;
    private pageNumber = 1;

    @Ref("tableBlock") protected tableBlock: any;

    private fields: any[] = [
        {
            key: "prescriber",
            label: "Prescriber"
        },
        {
            key: "patient",
            label: "Patient Name"
        },
        { key: "prescription", label: "Rx Id", formatter: (p: Prescription) => `${p.storeID}-${p.rxNumber}-${p.rfNumber}` },
        { key: "prescription.productNameShort", label: "Drug Name", },
        { key: "created", label: "Initiated", formatter: (d: any) => moment.utc(d).local().format("l LT"), sortable: true },
        { key: "updated", label: "Next Touch", formatter: this.formatNextTouch, sortable: true },
        { key: "latestCallback", label: "CMM Event", formatter: this.cmmEventFormatter, },
        { key: "type", formatter: (d: PriorAuthorizationType) => PriorAuthorizationType[d] },
        { key: "statusName", label: "Status" },
        { key: "completeReason", label: "Reason", },
    ];

    formatDate(d: Date) {
        return moment(d).format("L");
    }

    findNextTouchMatch(nextTouchDays: PriorAuthNextTouch[], status: PriorAuthorizationStatusCode, type?: PriorAuthorizationType): PriorAuthNextTouch | undefined {
        return nextTouchDays.find(i => i.status === status && i.type === type) ||
            nextTouchDays.find(i => i.type === type && i.status == null) ||
            nextTouchDays.find(i => i.status === status && i.type == null) ||
            nextTouchDays.find(i => i.status == null && i.type == null);
    }

    formatNextTouch(d: Date, _: string, item: PriorAuthorization): string {
        const { status, type } = item;
        const programId = item.prescription?.programID;
        const storeId = item.prescription?.storeID;
        const programMatch = this.programNextTouchDays.find(p => p.id === programId);

        /// if there is a program configuration and nextTouch return it; otherwise, fall back on the global next touch; else add 1 day
        const programNextTouchMatch = programMatch?.priorAuthNextTouchDays ? this.findNextTouchMatch(programMatch.priorAuthNextTouchDays, status, type) : undefined;
        const bestMatch = programNextTouchMatch ? programNextTouchMatch: this.findNextTouchMatch(this.nextTouchDays, status, type);
        const daysToAdd = bestMatch?.days ?? 1;

        const startDate = moment.utc(d).local();

        const businessHours = storeId ? this.storeBusinessHours.get(storeId) : undefined;

        return addBusinessDays(startDate, daysToAdd, businessHours).format("l LT");
    }




    cmmEventFormatter(cb: CoverMyMedsCallback) {
        return cb?.event_key ? cb.event_key : cb?.error ?? "";
    }

    get totalRows() {
        return this.filteredPriorAuthorizations?.length ?? 0;
    }

    get filteredPriorAuthorizations() {
        const allManufact = this.selectedManufacturer === "All";
        const allStatus = !this.status && this.status != "0";
        const allTypes = !this.selectedType && this.selectedType != "0";
        const allStores = !this.store?.id;
        const allPrograms = !this.program?.id;
        const queryTerms = this.filterQuery?.toLowerCase().split(" ");
        const hasQueryTerms = (this.filterQuery?.trim().length ?? 0) > 0;

        return this.priorAuthorizations.filter(item => {
            const manufact = allManufact || item.drugManufacturer == this.selectedManufacturer;
            const status = allStatus || item.status.toString() == this.status;
            const type = allTypes || (item.type !== undefined && item.type !== null && item.type.toString() == this.selectedType);
            const store = allStores || item.prescription.storeID == this.store.id;
            const program = allPrograms || item.prescription.programID == this.program.id;
            const filterMatch = !hasQueryTerms || queryTerms!.every((qt: string) =>
                item.patient?.firstName?.toLowerCase().includes(qt) ||
                item.patient?.lastName?.toLowerCase().includes(qt) ||
                item.prescription?.drugName?.toLowerCase().includes(qt) ||
                item.prescription?.packageID?.toString().includes(qt) ||
                item.completeReason?.toLowerCase().includes(qt) ||
                item.prescription.rxNumber?.toString().includes(qt) ||
                item.statusName.toLowerCase().includes(qt) ||
                item.prescriber?.lastName?.toLowerCase().includes(qt) ||
                item.prescriber?.firstName?.toLowerCase().includes(qt) ||
                this.cmmEventFormatter(item.latestCallback).includes(qt));

            return store && program && manufact && status && type && filterMatch;
        });
    }

    resetPage() {
        this.pageNumber = 1;
    }

    @Watch("pageNumber")
    resetTableScrollPosition() {
        this.tableBlock?.scrollIntoView({ behavior: "smooth" });
    }

    async fetchPriorAuthorizations() {
        try {
            const url = `/PriorAuthorization/`;

            this.isLoading = true;
            const response = await this.$http.get<Array<PriorAuthorization>>(url);

            this.priorAuthorizations = response.data?.map(pa => new PriorAuthorization(pa));
            this.resetPage();
        } catch (err) {
            console.error("Error loading PAs.", err);
            this.$notification(NotificationOptions.error(err));
        }

        this.isLoading = false;
    }

    get manufacturers() {
        return ["All", ...this.priorAuthorizations.map(p => p.drugManufacturer).filter(this.onlyUnique).sort()];
    }

    get manufacturersByStatus() {
        // Hover text for manufacturers status.
        const groupedList = this.priorAuthorizations.reduce((group: any, pa) => {
            const { status } = pa;
            group[status] = group[status] ?? [];
            group[status].push(pa);
            return group;
        }, {});

        const group = Object.getOwnPropertyNames(groupedList).reduce((grp: any, status: any) => {
            grp[status] = groupedList[status].reduce((group: any, pa: any) => {
                const { drugManufacturer } = pa;
                if (!group.hasOwnProperty(drugManufacturer)) {
                    group[drugManufacturer] = 0;
                }
                group[drugManufacturer]++;
                return group;
            }, {})
            return grp;
        }, {});

        return Object.getOwnPropertyNames(group).map((status: any) => {
            let manus = Object.getOwnPropertyNames(group[status]).sort();
            return { status: Number(status), text: manus.map((name: any) => `${name}: ${group[status][name]}`).join(' <br/> ') }
        });
    }

    private statusToIgnore = [
        PriorAuthorizationStatusCode.Abandoned,
        PriorAuthorizationStatusCode.Approved,
        PriorAuthorizationStatusCode.Denied,
        PriorAuthorizationStatusCode.Cancelled
    ];

    countByStatus(status: PriorAuthorizationStatusCode): number {
        const res = this.priorAuthorizations.filter(pa => pa.status == status)?.length || 0;
        return res;
    }

    get paStatusList(): Array<PriorAuthorizationStatusCode> {
        const res = new Array<PriorAuthorizationStatusCode>();

        for (const st in PriorAuthorizationStatusCode)
            if (Number(st) >= 0 && !this.statusToIgnore.some(st2 => st2 == Number(st)))
                res.push(Number(st) as PriorAuthorizationStatusCode);

        return res;
    }

    getManufacturersByStatusHoverText(status: PriorAuthorizationStatusCode): string {
        const res = this.manufacturersByStatus.find((m: any) => m.status == status)?.text;
        return res || '';
    }

    onlyUnique(value: any, index: any, self: any) {
        return self.indexOf(value) === index;
    }

    selectPa(item: PriorAuthorization) {
        this.selectedPa = item;
        this.$bvModal.show("pa-details-modal");
    }

    protected nextTouchDays: PriorAuthNextTouch[] = [];
    protected programNextTouchDays: Program[] = [];
    protected storeBusinessHours: Map<number, any> = new Map();

    async getNextTouchDaysFromConfig() {
        try {
            const resp = await this.$http.get<any>("/PriorAuthorization/NextTouchDays");
            this.programNextTouchDays = resp.data.programs;
            this.nextTouchDays = resp.data.defaultNextTouchDays;

            const storesResp = await this.$http.get<any>("/Store/BusinessHours");
            if (storesResp.data) {
                storesResp.data.forEach((store: any) => {
                    if (store.businessHours) {
                        this.storeBusinessHours.set(store.id, store.businessHours);
                    }
                });
            }
        } catch (err) {
            console.error(err);
        }
    }
}
</script>

<style scoped>
.longToolTip::v-deep .tooltip-inner {
    max-width: 100%;
}

.stat-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
    text-align: center;
}

.stat-grid h4 {
    margin-top: 0;
}

.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
}

@media (min-width: 1600px) {
    .stat-grid {
        grid-template-columns: repeat(5, 1fr);
    }

    .filter-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
